import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Inject token into every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('axisvms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('axisvms_token');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// Auth
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  me: () => api.get('/auth/me'),
  setTimezone: (timezone) => api.patch('/auth/me', { timezone }),
  updateProfile: (data) => api.patch('/auth/me', data),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
  createUser: (data) => api.post('/auth/users', data),
  uploadAvatar: (image) => api.post('/auth/avatar', { image }),
  removeAvatar: () => api.delete('/auth/avatar'),
};

// Companies
export const companyApi = {
  list: () => api.get('/companies'),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Users (admin management)
export const userApi = {
  list: () => api.get('/auth/users'),
  create: (data) => api.post('/auth/users', data),
  update: (id, data) => api.put(`/auth/users/${id}`, data),
  delete: (id) => api.delete(`/auth/users/${id}`),
  uploadAvatar: (id, image) => api.post(`/auth/users/${id}/avatar`, { image }),
  removeAvatar: (id) => api.delete(`/auth/users/${id}/avatar`),
};

// Cameras
export const cameraApi = {
  list: () => api.get('/cameras'),
  get: (id) => api.get(`/cameras/${id}`),
  create: (data) => api.post('/cameras', data),
  update: (id, data) => api.put(`/cameras/${id}`, data),
  delete: (id) => api.delete(`/cameras/${id}`),
  tzFromGps: (lat, lng) => api.get('/cameras/tz-from-gps', { params: { lat, lng } }),
  listGroups: () => api.get('/cameras/groups/all'),
  createGroup: (data) => api.post('/cameras/groups', data),
  updateGroup: (id, data) => api.put(`/cameras/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/cameras/groups/${id}`),
};

// VAPIX
// PTZ — one API for every camera brand.
//
// Replaces vapixApi/dahuaApi/hikvisionApi and the getPtzApi(manufacturer) switch. The
// backend resolves the brand from the camera record and dispatches to its driver, so the
// client no longer needs to know what a camera is to talk to it.
export const ptzApi = {
  connect:                (id) => api.post(`/ptz/${id}/connect`),
  reboot:                 (id) => api.post(`/ptz/${id}/reboot`),
  ptzPosition:            (id) => api.get(`/ptz/${id}/ptz/position`),
  ptzMove:                (id, data) => api.post(`/ptz/${id}/ptz/move`, data),
  ptzStop:                (id) => api.post(`/ptz/${id}/ptz/stop`),
  ptzAbsolute:            (id, data) => api.post(`/ptz/${id}/ptz/absolute`, data),
  ptzCenter:              (id, data) => api.post(`/ptz/${id}/ptz/center`, data),
  gotoHome:               (id) => api.post(`/ptz/${id}/ptz/home`),
  ptzPresets:             (id) => api.get(`/ptz/${id}/ptz/presets`),
  gotoPreset:             (id, presetId) => api.post(`/ptz/${id}/ptz/preset/${presetId}`),
  savePreset:             (id, name) => api.post(`/ptz/${id}/ptz/preset/save`, { name }),
  capturePresetThumbnail: (id, presetId) => api.post(`/ptz/${id}/ptz/presets/${presetId}/thumbnail`),
  updatePresetPosition:   (id, presetId, name) => api.post(`/ptz/${id}/ptz/preset/${presetId}/updatepos`, { name }),
  // Static per-driver descriptors — not camera-scoped, so the add-camera form can use it
  // before a camera exists. See hooks/useCameraDrivers.
  drivers:                () => api.get('/ptz/drivers'),
  ptzLimits:              (id) => api.get(`/ptz/${id}/ptz/limits`),
  calibrateZoom:          (id) => api.post(`/ptz/${id}/ptz/calibrate-zoom`, {}, { timeout: 70000 }),

  getTours:  (id) => api.get(`/ptz/${id}/ptz/tours`),
  startTour: (id, tourId) => api.post(`/ptz/${id}/ptz/tours/activate`, { tourId }),
  stopTour:  (id, tourId) => api.post(`/ptz/${id}/ptz/tours/stop`, { tourId }),

  getAudio: (id) => api.get(`/ptz/${id}/audio`),
  setAudio: (id, data) => api.post(`/ptz/${id}/audio`, data),
};

// Snapshots — capture returns the blob directly (single request)
export const snapshotApi = {
  capture: (id) => api.post(`/snapshots/${id}`, {}, { responseType: 'blob' }),
  liveUrl: (id) => `/api/snapshots/${id}/live?token=${localStorage.getItem('axisvms_token')}`,
  list: (id) => api.get(`/snapshots/${id}/list`),
};

// Live stream negotiation. The SDP exchange goes through our backend rather than direct
// to go2rtc so that per-camera authorization is applied — go2rtc has none of its own.
export const streamApi = {
  capabilities: (id) => api.get(`/stream/${id}/capabilities`),
  webrtcOffer: (id, sdp) => api.post(`/stream/${id}/webrtc`, { sdp }),
};

// Stored presets (AxisVMS local data store)
export const presetApi = {
  list: (cameraId) => api.get(`/cameras/${cameraId}/presets`),
  save: (cameraId, presets) => api.put(`/cameras/${cameraId}/presets`, presets),
  update: (cameraId, presetId, data) => api.put(`/cameras/${cameraId}/presets/${presetId}`, data),
  remove: (cameraId, presetId) => api.delete(`/cameras/${cameraId}/presets/${presetId}`),
};

// Audit & Events
export const auditApi = {
  getLog: (limit = 200, cameraId = null, action = null) => api.get('/audit/log', { params: { limit, cameraId, action } }),
  getEvents: (cameraId, limit = 200) => api.get('/audit/events', { params: { cameraId, limit } }),
};

// Operator schedule (calendar)
export const scheduleApi = {
  list: (cameraId, from, to) => api.get('/schedule', { params: { cameraId, from, to } }),
  book: (cameraId, slotStarts, operatorId) => api.post('/schedule', { cameraId, slotStarts, operatorId }),
  remove: (id) => api.delete(`/schedule/${id}`),
};

// Controller Notes (per-camera timestamped notes)
export const notesApi = {
  list:   (cameraId) => api.get(`/notes/${cameraId}`),
  create: (cameraId, data) => api.post(`/notes/${cameraId}`, data), // { text, includeSnapshot }
  remove: (cameraId, noteId) => api.delete(`/notes/${cameraId}/${noteId}`),
};

// Notifications (notification center)
export const notificationApi = {
  list: () => api.get('/notifications'),
  markAllRead: () => api.post('/notifications/read'),
  create: (data) => api.post('/notifications', data), // admin only
  update: (id, data) => api.put(`/notifications/${id}`, data), // admin only
  delete: (id) => api.delete(`/notifications/${id}`), // admin only
};

export default api;
